import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Quote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { HERO_IMAGE, LOGO_URL, MIAMI_IMAGE, SHRINE_IMAGE } from "../assets/images";
import MTAGreeting from "../shared/MTAGreeting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map well-known slugs to their dedicated public page; otherwise fallback to /eventos/{slug}.
const EVENT_ROUTES = {
  "apresentacao-setembro-2026": "/apresentacao-setembro-2026",
  "congresso-miami-2026": "/congresso-miami-2026",
};
const eventHref = (slug) => EVENT_ROUTES[slug] || `/eventos/${slug}`;

// Image fallback for events that don't have their own image_url
const FALLBACK_EVENT_IMAGE = MIAMI_IMAGE;

function localized(ev, lang, key) {
  return ev[`${key}_${lang}`] || ev[`${key}_pt`] || ev[key] || "";
}

export default function Home() {
  const { t, lang } = useLang();
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    // Fetch 3 so we can decide whether to render a "see all" card.
    axios.get(`${API}/events/upcoming?limit=3`)
      .then((r) => setUpcoming(r.data.items || []))
      .catch(() => setUpcoming([]));
  }, []);

  const heroEvent = upcoming[0];
  const heroCta = heroEvent
    ? (localized(heroEvent, lang, "title") || t.home.ctaPrimary)
    : t.home.ctaPrimary;
  const heroHref = heroEvent ? eventHref(heroEvent.slug) : "/eventos";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden
          />
          <div className="absolute inset-0 bg-[hsl(var(--primary))]/85" />
          <div className="absolute inset-0 grain pointer-events-none" />
        </div>

        <div className="relative container-page pt-28 pb-28 sm:pt-36 sm:pb-36 text-white">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 fade-in">
              <span className="accent-rule" />
              <span className="overline text-white/85">{t.home.overline}</span>
            </div>
            <h1
              className="display-heading text-5xl sm:text-6xl lg:text-7xl mt-6 fade-in-up"
              data-testid="hero-title"
            >
              {t.home.heroTitle}
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed fade-in-up">
              {t.home.heroSubtitle}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 fade-in-up">
              <Link
                to={heroHref}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[hsl(var(--accent))] text-[#1A201F] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#B3922E] transition-all hover:translate-y-[-1px]"
                data-testid="hero-cta-primary"
              >
                {heroCta} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/40 text-white text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-[hsl(var(--primary))] transition-all"
                data-testid="hero-cta-secondary"
              >
                {t.home.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SAUDAÇÃO DA MTA */}
      <MTAGreeting />

      {/* MISSION / PILLARS */}
      <section className="container-page py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="overline">{t.home.sectionAboutOverline}</div>
            <h2 className="display-heading text-4xl sm:text-5xl mt-5 leading-[1.1]">
              {t.home.sectionAboutTitle}
            </h2>
            <span className="accent-rule mt-8 block" />
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
              {t.home.sectionAboutBody}
            </p>
            <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-12">
              {t.home.pillars.map((p, i) => (
                <div key={i} className="border-t border-[hsl(var(--border))] pt-6">
                  <div className="text-[hsl(var(--accent))] text-xs tracking-[0.3em] font-semibold">
                    0{i + 1}
                  </div>
                  <h3 className="mt-3 display-heading text-2xl">{p.title}</h3>
                  <p className="mt-3 text-[15px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="bg-[hsl(var(--secondary))] py-24 sm:py-32">
        <div className="container-page">
          <div className="max-w-3xl">
            <div className="overline">{t.home.eventsOverline}</div>
            <h2 className="display-heading text-4xl sm:text-5xl mt-5">{t.home.eventsTitle}</h2>
            <span className="accent-rule mt-8 block" />
          </div>

          <div className={`mt-16 grid gap-8 ${upcoming.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`} data-testid="home-upcoming-events">
            {upcoming.length === 0 && (
              <p className="text-[hsl(var(--muted-foreground))] lg:col-span-2">
                {t.home.eventsEmpty || "Em breve serão anunciados novos eventos."}
              </p>
            )}
            {upcoming.slice(0, 2).map((ev, idx) => {
              const title = localized(ev, lang, "title");
              const subtitle = localized(ev, lang, "subtitle");
              const loc = ev.location || "";
              const ds = ev.date_str || "";
              // Avoid duplicating the location when date_str already mentions it.
              const dateLine = (ds && loc && !ds.toLowerCase().includes(loc.toLowerCase())
                ? `${ds} · ${loc}` : (ds || loc)).toUpperCase();
              const href = eventHref(ev.slug);
              const cta = idx === 0 ? (t.home.septCard?.cta || "Saber mais") : (t.home.miamiCard?.cta || "Saber mais");
              // First card → light layout. Second card → image overlay.
              if (idx === 0 || !ev.image_url) {
                return (
                  <Link key={ev.slug} to={href}
                    className="group block bg-white border border-[hsl(var(--border))] p-8 sm:p-10 hover:border-[hsl(var(--primary))] transition-all duration-300"
                    data-testid={`event-card-${ev.slug}`}>
                    <div className="text-[hsl(var(--accent))] text-xs tracking-[0.3em] font-semibold uppercase">
                      {dateLine || (ev.date_iso || "Em breve")}
                    </div>
                    <h3 className="mt-4 display-heading text-3xl sm:text-4xl group-hover:text-[hsl(var(--primary))] transition-colors">
                      {title}
                    </h3>
                    {subtitle && (
                      <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">
                        {subtitle}
                      </p>
                    )}
                    <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-medium">
                      {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                );
              }
              return (
                <Link key={ev.slug} to={href}
                  className="group relative overflow-hidden block border border-[hsl(var(--border))]"
                  data-testid={`event-card-${ev.slug}`}>
                  <img src={ev.image_url || FALLBACK_EVENT_IMAGE} alt=""
                    className="w-full h-full object-cover absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                    aria-hidden />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E4D45]/95 via-[#0E4D45]/70 to-[#0E4D45]/30" />
                  <div className="relative p-8 sm:p-10 text-white min-h-[340px] flex flex-col justify-end">
                    <div className="text-[hsl(var(--accent))] text-xs tracking-[0.3em] font-semibold uppercase">
                      {dateLine || (ev.date_iso || "Em breve")}
                    </div>
                    <h3 className="mt-4 display-heading text-3xl sm:text-4xl">{title}</h3>
                    {subtitle && <p className="mt-5 text-white/85 leading-relaxed">{subtitle}</p>}
                    <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-medium">
                      {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
            {upcoming.length >= 3 && (
              <Link to="/eventos"
                className="group relative block bg-[hsl(var(--primary))] text-white p-8 sm:p-10 min-h-[340px] flex flex-col justify-between overflow-hidden hover:bg-[#093630] transition-colors"
                data-testid="event-card-see-all">
                <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[hsl(var(--accent))]/15 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative">
                  <div className="text-[hsl(var(--accent))] text-xs tracking-[0.3em] font-semibold uppercase">
                    {t.home.eventsOverline}
                  </div>
                  <div className="mt-4 display-heading text-3xl sm:text-4xl leading-tight">
                    Ver todos<br />os eventos.
                  </div>
                  <p className="mt-5 text-sm text-white/75 leading-relaxed max-w-xs">
                    {(upcoming.length - 2) === 1
                      ? "Mais 1 evento na agenda — encontros, retiros e formações em preparação."
                      : `Mais ${upcoming.length - 2}+ eventos na agenda — encontros, retiros e formações em preparação.`}
                  </p>
                </div>
                <div className="relative mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-medium">
                  Agenda completa <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* QUOTE / SPIRITUALITY */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={SHRINE_IMAGE} alt="" className="w-full h-full object-cover" aria-hidden />
          <div className="absolute inset-0 bg-[#1A201F]/75" />
        </div>
        <div className="relative container-narrow py-28 sm:py-36 text-center text-white">
          <Quote className="h-10 w-10 text-[hsl(var(--accent))] mx-auto" />
          <div className="overline text-white/70 mt-6">{t.home.quoteOverline}</div>
          <blockquote className="display-heading text-3xl sm:text-4xl lg:text-5xl mt-6 leading-tight italic">
            {t.home.quote}
          </blockquote>
          <div className="mt-8 text-sm tracking-[0.2em] uppercase text-white/70">
            — {t.home.quoteAuthor}
          </div>
          <div className="mt-10 flex justify-center">
            <img src={LOGO_URL} alt="CIEES" className="h-16 opacity-80" />
          </div>
        </div>
      </section>
    </div>
  );
}
